package com.essntl.features.crm.data.repository

import com.essntl.core.utils.repository.Repository
import com.essntl.core.utils.utils.letIfNotEmpty
import com.essntl.features.address.domain.model.AddressModel
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.crm.data.supabase.OrganizationListDto
import com.essntl.features.crm.data.supabase.CreateContactDto
import com.essntl.features.crm.domain.model.OrganizationListModel
import com.essntl.features.crm.domain.model.CreateContactModel
import com.essntl.features.crm.domain.repository.CRMRepository
import com.essntl.features.customfields.domain.model.CustomFieldsModel
import com.essntl.features.organization.data.supabase.toDto
import com.essntl.features.organization.domain.model.OrganizationModel
import core.dateformat.utils.fromDDTOMM
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.functions.functions
import io.ktor.client.call.body
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonObject
import org.koin.core.annotation.Single

@Single(binds = [CRMRepository::class])
internal class CRMRepositoryImpl(
    private val supabase: SupabaseClient,
) : CRMRepository, Repository() {

    override suspend fun getOrganizationList(
        type: String,
        searchText: String,
    ): Result<List<OrganizationListModel>> =
        runCatching {
            supabase.functions.invoke(
                function = "list-organizations?type=$type&searchTerm=$searchText",
            ).body<List<OrganizationListDto>>()
                .map { dto -> dto.toModel() }
        }

    override suspend fun createContactOrganization(
        organization: OrganizationModel,
        organizationAddress: AddressModel?,
        contact: ContactModel,
        contactAddress: AddressModel?,
        customFields: CustomFieldsModel?,
    ): Result<CreateContactModel> =
        runCatching {
            supabase.functions.invoke(
                function = "create-contact-org",
                body = buildJsonObject {
                    if (organization.id.isEmpty())
                        putJsonObject("organization") {
                            put("name", organization.name)
                            put("type", organization.type?.toDto())
                            organization.website?.letIfNotEmpty { put("website", it) }
                            organization.logoFileId?.letIfNotEmpty { put("logo_file_id", it) }
                            organization.parentId?.letIfNotEmpty { put("parent_id", it) }

                            if (organizationAddress != null)
                                putJsonObject("address") {
                                    put("line_1", organizationAddress.line1)
                                    put("city", organizationAddress.city)
                                    put("country", organizationAddress.country)
                                    organizationAddress.line2?.letIfNotEmpty { put("line_2", it) }
                                    organizationAddress.state?.letIfNotEmpty { put("state", it) }
                                    organizationAddress.zipCode?.letIfNotEmpty { put("zip_code", it) }
                                }
                        }

                    putJsonObject("contact") {
                        if (organization.id.isNotEmpty()) put("organization_id", organization.id)
                        contact.firstName?.letIfNotEmpty { put("first_name", it) }
                        contact.lastName?.letIfNotEmpty { put("last_name", it) }
                        contact.email?.letIfNotEmpty { put("email", it) }
                        contact.phoneNumber?.letIfNotEmpty { put("phone_number", it) }
                        contact.notes?.letIfNotEmpty { put("notes", it) }
                        contact.salutation?.letIfNotEmpty { put("salutation", it) }
                        contact.dateOfBirth?.letIfNotEmpty { put("date_of_birth", fromDDTOMM(it)) }

                        if (contactAddress != null)
                            putJsonObject("address") {
                                put("line_1", contactAddress.line1)
                                put("city", contactAddress.city)
                                put("country", contactAddress.country)
                                contactAddress.line2?.letIfNotEmpty { put("line_2", it) }
                                contactAddress.state?.letIfNotEmpty { put("state", it) }
                                contactAddress.zipCode?.letIfNotEmpty { put("zip_code", it) }
                            }
                    }

                    if (customFields != null)
                        if (organization.type == OrganizationModel.Type.TravelAgency) {
                            putJsonObject("travel_agent") {
                                customFields.commission?.let {
                                    put("commission", it)
                                    put(
                                        "commission_type",
                                        (customFields.commissionType
                                            ?: CustomFieldsModel.CommissionType.Gross).name.uppercase(),
                                    )
                                }

                                customFields.source?.letIfNotEmpty { put("source", it) }
                            }
                        } else if (organization.type == OrganizationModel.Type.Supplier) {
                            putJsonObject("supplier") {
                                customFields.category?.let { put("category", it.id) }
                                customFields.area?.let { put("area", it.id) }
                                customFields.description?.letIfNotEmpty { put("description", it) }
                            }
                        }
                },
            ).body<CreateContactDto>()
                .toModel()
        }
}